import React from "react";
import PropTypes from "prop-types";
import "../styles/hero.css";

/**
 * HeroSection (Figma-aligned)
 * 
 * Implements the provided layout specs:
 * - Padding L/R: 110px, T/B: 234px
 * - Background: #243A24 with optional background image (cover)
 * - Headline: 56/66, bold
 * - Subheadline: 18/30
 * - Primary CTA: white pill (24/36 padding), dark-green text, arrow icon
 * - Secondary CTA: transparent pill with 1.5px white outline
 * 
 * A11y:
 * - Renders a single <h1> (ensure only one per page)
 * - Links are keyboard accessible with visible focus
 * 
 * TODO: Load DM Sans via global font import to match Figma exactly.
 */
const HeroSection = ({
  headline,
  subheadline,
  primaryCta,
  secondaryCta,
  backgroundImage, // e.g. "/media/hero.jpg"
  backgroundColor = "#243A24",
}) => {
  const styleVars = {
    "--hero-bg-color": backgroundColor,
    "--hero-bg-image": backgroundImage ? `url(${backgroundImage})` : "none",
  };

  return (
    <section className="hero figma-hero" style={styleVars}>
      <div className="hero-inner">
        <h1 className="hero-headline">{headline}</h1>
        {subheadline && <p className="hero-subheadline">{subheadline}</p>}

        {(primaryCta || secondaryCta) && (
          <div className="hero-cta-row">
            {primaryCta?.label && primaryCta?.href && (
              <a
                href={primaryCta.href}
                className="btn-hero btn-hero-primary"
                aria-label={primaryCta.label}
              >
                <span>{primaryCta.label}</span>
                {/* Right-arrow icon */}
                <svg
                  className="btn-hero-icon"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    d="M6 3l6 6-6 6"
                    fill="none"
                    stroke="#243A24"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </a>
            )}

            {secondaryCta?.label && secondaryCta?.href && (
              <a
                href={secondaryCta.href}
                className="btn-hero btn-hero-secondary"
                aria-label={secondaryCta.label}
              >
                <span>{secondaryCta.label}</span>
              </a>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

HeroSection.propTypes = {
  headline: PropTypes.string.isRequired,
  subheadline: PropTypes.string,
  primaryCta: PropTypes.shape({
    label: PropTypes.string.isRequired,
    href: PropTypes.string.isRequired,
  }),
  secondaryCta: PropTypes.shape({
    label: PropTypes.string.isRequired,
    href: PropTypes.string.isRequired,
  }),
  backgroundImage: PropTypes.string,
  backgroundColor: PropTypes.string,
};

export default HeroSection;

